<template>
  <div
    @mouseleave="handleMouseLeave"
    :id="'product-card-' + targetFromId + '-' + product.id"
    class="product-card-grid product-card-wrapper" ref="productCard">
    <div v-if="product.is_new_arrival" class="new-badge">
      <img :src="newBadgeImage" alt="" />
      <span class="text-white">NEW</span>
    </div>
    <a :href="productUrl" class="image">
      <img
        @error="handleImageError"
        :src="baseImage"
        :class="{ 'image-placeholder': !hasBaseImage }"
        alt="product image"
      />
    </a>
    <div class="description mt-auto">
      <span v-if="product.is_rare_find"
            class="text-green d-flex justify-content-center">Serial #: {{ product.serial_number }}</span>
      <h4 class="d-flex gap-10" data-toggle="tooltip" data-placement="top" :title="product.name">
        <a :href="productUrl" class="prod-name">{{ product.name }}</a>
        <a v-if="isAdmin" :href="`/admin/products/${product.id}`" target="_blank">
          <Edit2Icon />
        </a>
      </h4>
      <div class="hot-or-daily" :class="{ 'hot-or-daily--empty': !hasBadgeRow }" :aria-hidden="!hasBadgeRow">
        <div v-if="product.is_hot" class="hot-product-badge mr-0 width-fit-content">
          <img :src="hotImage" alt="" />
          <small>Hot Product</small>
        </div>
        <div class="limit-purchase-badge-stack">
          <div :id="'limitPurchaseLabel-' + targetFromId + '-' + product.id"
               v-if="product.is_purchase_limit"
               class="limit-purchase-badge mr-0 width-fit-content">
            <small>${{ this.dailyPurchaseLimitAmount }} per day</small>
          </div>
          <b-popover
            v-if="product.is_purchase_limit"
            :target="'limitPurchaseLabel-' + targetFromId + '-' + product.id"
            triggers="hover"
            placement="top"
            ref="purchaseLimitPopover"
          >
            <div>
              {{ purchaseLimitTooltipText }}
              <a href="#" @click.prevent="showDetailedTooltip" class="text-primary" style="text-decoration: underline; cursor: pointer;">more...</a>
            </div>
          </b-popover>
          <div
            v-else-if="shouldShowDemonetized"
            v-b-popover.hover.top="demonetizedTooltipText"
            class="limit-purchase-badge limit-purchase-badge--inactive mr-0 width-fit-content">
            <small>Demonetized</small>
          </div>
        </div>
        <div v-if="product.is_daily_deal_today" class="daily-deal-badge mr-0 width-fit-content">
          <small>Daily Deal ${{ currencyFormat(product.daily_deal_price) }} USD</small>
          <daily-deal-inline-editor
            v-if="isAdmin"
            :product-id="product.id"
            :product-name="product.name"
            :regular-price="product.price && product.price.amount"
            :is-daily-deal="product.is_daily_deal"
            :daily-deal-date="product.daily_deal_date"
            :daily-deal-price="product.daily_deal_price"
            @daily-deal-updated="onDailyDealEdited"
            @daily-deal-removed="onDailyDealRemoved"
          />
        </div>
        <div
          v-if="product.is_rare_find" class="rare-find-product-badge mr-0 width-fit-content">
          <small>RareFind</small>
        </div>
      </div>
    </div>
    <div class="details mb-4">
      <span>As low as</span>
      <div class="price" :class="{ 'price--no-daily-deal': !product.is_daily_deal_today }">
        <div v-if="product.is_daily_deal_today" class="price-stack">
          <editable-price :product-id="product.id" :current-price="product.price.amount" @price-updated="onPriceUpdated">
            <strong class="text-decoration-line-through price-old">${{ currencyFormat(product.price.formatted) }}</strong>
          </editable-price>
          <div class="price-main">
            <editable-price :product-id="product.id" :current-price="product.daily_deal_price" field="daily_deal_price" @price-updated="onDailyDealPriceUpdated">
              <span class="price-new">${{ currencyFormat(product.daily_deal_price) }}</span>
            </editable-price>
            <span class="text-muted each-label">
              <span>Each</span>
            </span>
          </div>
        </div>
        <div v-else class="price-main">
          <editable-price :product-id="product.id" :current-price="product.price.amount" @price-updated="onPriceUpdated">
            <strong>${{ currencyFormat(product.selling_price.formatted) }}</strong>
          </editable-price>
          <span class="text-muted each-label">
            <span>Each</span>
          </span>
        </div>
        <div class="ml-auto d-inline-flex">
          <img v-b-popover.hover.top="{content:earnedPointText,customClass:'points-popover'}" :src="coinImage" alt="" />
          <span class="ml-1">{{ product.points }}</span>
        </div>
      </div>
      <div v-if="product.variety" class="variety">
        <span>Variety:</span>
        <span class="tag">{{ product.variety }}</span>
      </div>
    </div>
    <div class="bottom">
      <b-overlay :show="apiLoading" spinner-variant="primary" blur=".3" opacity=".75" rounded="sm">
        <div class="add-to-cart">
          <div class="number-picker" :id="'number-picker-' + product.id">
            <b-popover
              placement="bottom"
              @click="showPop = false"
              triggers=""
              :target="'number-picker-' + product.id"
              :show.sync="showPop"
            >
              <span class="text-danger"> We have {{ maxSellingQty }} item(s) only in this item </span></b-popover
            >
            <input
              v-integer-only
              type="text"
              :value="productQty"
              :min="1"
              :max="maxSellingQty"
              id="qty"
              class="form-control input-number input-quantity"
              @input="updateQuantity($event.target.value)"
              @keydown.up="updateQuantity(productQty + 1)"
              @keydown.down="updateQuantity(productQty - 1)"
            />
            <span class="btn-wrapper">
              <button
                type="button"
                @click="updateQuantity(productQty + 1)"
                class="btn btn-number btn-plus"
                :disabled="productQty >= maxSellingQty"
                data-type="plus"
              >
                +
              </button>
              <button
                type="button"
                :disabled="productQty >= maxSellingQty || productQty == 1"
                @click="updateQuantity(productQty - 1)"
                class="btn btn-number btn-minus text-danger"
                data-type="minus"
              >
                -
              </button>
            </span>
          </div>
          <button
            v-if="hasNoOption || product.is_out_of_stock"
            class="btn btn-primary btn-add-to-cart"
            :class="{ 'btn-loading': addingToCart, 'btn-pre-order': preOrder }"
            :disabled="product.is_out_of_stock || productQty == '' || productQty == 0 || giftProduct || variantProduct"
            @click="addToCart"
          >
            <span v-if="preOrder">PRE-ORDER</span>
            <span v-else>{{ $trans("storefront::product_card.add_to_cart") }}</span>
          </button>
        </div>
      </b-overlay>
      <div class="rating">
        <ProductRating :ratingPercent="product.rating_percent" :reviewCount="product.reviews_count || 0" :productId="product.id" :sku="product.sku" :href="productUrl + '#reviews'" />
        <button
          class="btn btn-wishlist p-0"
          :class="{ added: inWishlist }"
          :title="$trans('storefront::product_card.wishlist')"
          @click="syncWishlist"
        >
          <i class="la-heart" :class="inWishlist ? 'las' : 'lar'"></i>
        </button>
      </div>
    </div>
    <div v-show="false">{{ fields }}</div>
    <!--    <div v-if="fields.length > 1 && product.qd_enabled && product.quantity_discount_with_min_max_front.length"-->
    <!--         class="qd-details">-->
    <!--      <QD :product-info-page="false" :methods="fields" :price="product.price" :items="product.quantity_discount_with_min_max_front" />-->
    <!--    </div>-->
    <b-modal
      v-if="product.is_purchase_limit"
      v-model="showDetailedPopover"
      centered
      size="lg"
      hide-footer
      modal-class="purchase-limit-modal"
    >
      <template #modal-header="{ close }">
        <button
          type="button"
          class="close-btn"
          @click="close()"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </template>
      <div class="modal-content-wrapper">
        <p v-html="hotProductsDisclaimer"></p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ProductRating from "./ProductRating.vue"
import ProductCardMixin from "../mixins/ProductCardMixin"
import QD from "./QD"
import { currencyFormat } from "../functions"
import DefaultImageMixin from "@core/mixin/DefaultImageMixin"
import { Edit2Icon } from "vue-feather-icons"
import store from "../store"
import Vue from "vue"

export default {
  components: { QD, ProductRating, Edit2Icon },
  mixins: [ProductCardMixin, DefaultImageMixin],
  mounted() {
    // Filtring only Credit Card (Authorize.net) and Wire Transfer
    const allowedGateways = ['authorizenet', 'wire_transfer'];

    this.payment_methods
      .filter(method => allowedGateways.includes(method.payment_gateway_name))
      .forEach((paymentMethod) => {
        this.fields.push(
          {
            key: paymentMethod.vendors,
            label: paymentMethod.vendors,
            discount_percentage: paymentMethod.discount_percentage
          }
        )
      })
  },
  data() {
    return {
      popoverElement: null,
      isHovering: false, // Track hover state globally
      showDetailedPopover: false,
      fields: [
        { key: "qty", label: "Quantity", type: "Range" }
      ],
      currencyFormat,
      targetFromId: ""
    }
  },
  props: ["product", "isAdmin", "dailyPurchaseLimitAmount", "hotProductsDisclaimer", "targetFromId", "payment_methods"],
  computed: {
    earnedPointText() {
      return `Buy this product and earn up to ${this.product.points} reward points`
    },
    cart() {
      return store.state.cart
    },
    purchaseLimitTooltipText() {
      return "There is a $" + this.dailyPurchaseLimitAmount + " USD per person, per day limit for any combination of items that are specifically marked with the red $" + this.dailyPurchaseLimitAmount + " USD per day tag. This limit does not apply to any banknotes that do not have this tag."
    },
    demonetizedTooltipText() {
      return "This authentic Central Bank-issued banknote is demonetized and carries no monetary value. Sold by Banknote World as a collectible only."
    },
    shouldShowDemonetized() {
      return !this.product.is_purchase_limit && !this.product.is_bullion
    },
    giftProduct() {
      return store.state.cart.gift_products.find(item => item.id == this.product.id) != undefined
    },
    variantProduct() {
      return this.product.is_variant_enabled
    },
    hasBadgeRow() {
      return this.product.is_hot ||
        this.product.is_purchase_limit ||
        this.shouldShowDemonetized ||
        this.product.is_daily_deal_today ||
        this.product.is_rare_find
    }
  },
  methods: {
    showPopover() {
      if (this.product.is_daily_deal_today) return

      this.hoverOnCard = true
      if (this.popoverElement) return

      const rect = this.$el.getBoundingClientRect()
      console.log("rect", rect)

      // Create a container for the popover
      this.popoverElement = document.createElement("div")
      this.popoverElement.className = "product-hover-popover"
      this.popoverElement.style.position = "absolute"
      this.popoverElement.style.top = `${rect.bottom + window.scrollY - 6}px`
      this.popoverElement.style.left = `${rect.left + window.scrollX}px`
      this.popoverElement.style.width = `${rect.width}px`
      this.popoverElement.style.zIndex = "9999"
      this.popoverElement.style.background = "#fff"
      this.popoverElement.style.borderLeft = "1.5px solid #cfcfcf"
      this.popoverElement.style.borderRight = "1.5px solid #cfcfcf"
      this.popoverElement.style.borderBottom = "1.5px solid #cfcfcf"
      // this.popoverElement.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
      this.popoverElement.style.padding = "10px"
      this.popoverElement.style.borderBottomLeftRadius = "6px"
      this.popoverElement.style.borderBottomRightRadius = "6px"

      // Append the container to the body
      document.body.appendChild(this.popoverElement);

      // Dynamically render the QD component
      const QDComponent = Vue.extend(QD);
      const instance = new QDComponent({
        propsData: {
          productInfoPage: false,
          methods: this.fields,
          price: this.product.price,
          items: this.product.quantity_discount_with_min_max_front,
        },
      });

      // Mount the QD component and append it to the popover container
      instance.$mount();
      this.popoverElement.appendChild(instance.$el);

      // Store the instance for cleanup
      this.popoverInstance = instance;

      // Add event listeners to the popover for hover handling
      this.popoverElement.addEventListener("mouseenter", this.handleMouseEnterPopover);
      this.popoverElement.addEventListener("mouseleave", this.handleMouseLeavePopover);
    },
    hidePopover() {
      if (!this.isHovering && this.popoverElement) {
        // Remove the dynamic component
        this.popoverInstance.$destroy();
        document.body.removeChild(this.popoverElement);
        this.popoverElement = null;
        this.popoverInstance = null;
      }
    },
    handleMouseLeave() {
      this.isHovering = false;
      setTimeout(() => this.hidePopover(), 100); // Add slight delay to allow hover transition
    },
    handleMouseEnterPopover() {
      this.isHovering = true; // Keep popover visible
    },
    handleMouseLeavePopover() {
      this.isHovering = false;
      setTimeout(() => this.hidePopover(), 100); // Add slight delay to handle smooth exit
    },
    showDetailedTooltip() {
      if (this.$refs.purchaseLimitPopover) {
        this.$refs.purchaseLimitPopover.$emit('close');
      }
      this.showDetailedPopover = true;
    },
    onPriceUpdated(data) {
      this.$set(this.product.price, 'amount', data.price)
      this.$set(this.product.price, 'formatted', data.formatted_price)
      this.$set(this.product.selling_price, 'amount', data.selling_price)
      this.$set(this.product.selling_price, 'formatted', data.selling_price_formatted)
    },
    onDailyDealPriceUpdated(data) {
      this.$set(this.product, 'daily_deal_price', data.daily_deal_price)
    },
    onDailyDealEdited(data) {
      this.$set(this.product, 'is_daily_deal', data.is_daily_deal)
      this.$set(this.product, 'daily_deal_date', data.daily_deal_date)
      this.$set(this.product, 'daily_deal_price', data.daily_deal_price)
      this.$set(this.product, 'is_daily_deal_today', data.is_daily_deal_today)
    },
    onDailyDealRemoved(productId) {
      this.$emit('daily-deal-removed', productId)
    },
  },
  beforeDestroy() {
    if (this.popoverElement) {
      this.popoverInstance.$destroy()
      document.body.removeChild(this.popoverElement)
    }
  }
}
</script>

<style lang="scss">
.scrollable-popover .popover-content strong {
  font-weight: bold;
  color: inherit;
}

.scrollable-popover .popover-content {
  max-height: 500px;
  max-width: 800px;
  overflow-y: auto;
  overflow-x: hidden;
}

.btn-pre-order {
  background: #dc3545 !important;
}

.width-fit-content {
  width: fit-content;
}

.product-card-grid {
  display: flex !important;
  height: 100%;
  width: 100%;
  flex-direction: column;
  padding: 16px;
  border: 1.5px solid #cfcfcf;
  border-radius: 8px;
  position: relative;

  .new-badge {
    position: absolute;
    top: -2px;
    left: -2px;
    display: flex;

    span {
      position: absolute;
      transform: rotate(-45deg);
      top: 12px;
      left: 6px;
      font-size: 14px;
      font-weight: bold;
    }
  }

  .image {
    height: 250px;
    cursor: pointer;

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .description {
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    .hot-or-daily {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      align-items: flex-start;
      margin: 0 0 12px 0;
      margin-top: auto;
      // min-height: 90px;

      .rare-find-product-badge,
      .limit-purchase-badge,
      .hot-product-badge,
      .daily-deal-badge {
        display: inline-flex;
        align-items: center;
        border-radius: 22px;
        padding: 3px 10px;
        gap: 4px;

        img {
          width: 14px;
        }

        small {
          color: #ffffff;
          font-size: 11px;
        }
      }

      .hot-product-badge {
        background: #ff5656;
      }

      .daily-deal-badge {
        background: #40c887;
      }

      .limit-purchase-badge {
        background: red;
      }

      .limit-purchase-badge--inactive {
        background: #6c757d;
      }

      .rare-find-product-badge {
        background: green;
      }

      .limit-purchase-badge-stack {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      &.hot-or-daily--empty {
        visibility: hidden;
        pointer-events: none;
      }
    }

    > h4 {
      .prod-name {
        max-width: 100%;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.3;
        min-height: calc(3 * 1.3em);
      }
    }
  }

  h5 {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 0;
    min-height: 48px;
  }

  .details {
    .price {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      font-size: 16px;

      strong {
        //font-size: 24px;
        color: #DEAA5E;
        font-weight: 700;
      }

      .price-stack {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        line-height: 1.2;
        gap: 4px;
      }

      .price-main {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .each-label {
        display: inline-flex;
      }

      .price-old {
        color: #9b9b9b;
      }

      .price-new {
        color: #ff4d4f;
        font-weight: 700;
      }

      &.price--no-daily-deal {
        min-height: 42px;
      }
    }

    .variety {
      display: flex;
      flex-wrap: wrap;
      gap: 7px;
      margin-bottom: 8px;

      .tag {
        padding: 0 12px;
        font-size: 12px;
        display: flex;
        align-items: center;
        font-weight: 600;
        background-color: #f3f3f3;
        border-radius: 30px;
      }
    }
  }

  .bottom {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .add-to-cart {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      flex-wrap: nowrap;

      .number-picker {
        display: inline-flex;
        align-items: center;
        position: relative;
        width: 60%;
        height: 40px;
      }

      .number-picker label {
        font-weight: 500;
        margin-bottom: 0;
      }

      .number-picker .input-quantity {
        padding: 10px 39px 10px 10px;
        height: 40px;
        text-align: center;
        font-size: 13px;
        font-weight: bold;
      }

      .number-picker .input-quantity:focus {
        outline: 0;
        border-color: #ebebeb;
      }

      .number-picker .btn-wrapper {
        position: absolute;
        top: 0;
        right: 0;
      }

      .number-picker .btn-wrapper .btn-number {
        cursor: pointer;
        position: absolute;
        right: 0;
        width: 30px;
        padding: 0;
        line-height: 18px;
        background: none;
        border: 1px solid #ebebeb;
        border-radius: 0;
      }

      .number-picker .btn-wrapper .btn-number:hover {
        color: #DEAA5E;
      }

      .number-picker .btn-wrapper .btn-number[disabled]:hover {
        color: #191919;
      }

      .number-picker .btn-wrapper .btn-plus {
        top: 0;
        color: #DEAA5E;
        height: 20px;
        border-top-right-radius: 2px;
        border-bottom: 1px solid #ebebeb;
        border-left: 1px solid #ebebeb;
      }

      .number-picker .btn-wrapper .btn-minus {
        top: 20px;
        height: 20px;
        border-bottom-right-radius: 2px;
        border-left: 1px solid #ebebeb;
      }

      .btn-add-to-cart {
        padding: 12px 20px;
        width: 40%;
        flex: 1;
        line-height: 1.2;
        border-radius: 6px;
        font-size: 10px;
        font-weight: bold;
        white-space: nowrap;
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }
    }

    .rating {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}

.text-muted {
  color: #b5b5b5 !important;
}

.text-decoration-line-through {
  text-decoration: line-through !important;
}

.slick-list.draggable {
  z-index: 1;
}

.qd-details {
  position: absolute;
  height: 0;
  border-left: 1px solid #cfcfcf;
  border-right: 1px solid #cfcfcf;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  top: calc(100% - 10px);
  background-color: #fff;
  z-index: 100;
  overflow: hidden;
  left: -1px;
  right: -1px;
}

.product-card-grid:hover .qd-details {
  height: fit-content;
  border-bottom: 1px solid #cfcfcf;
}

.text-green {
  color: green;
}

.points-popover {
  min-width: fit-content;
  width: 100%;
}

.purchase-limit-modal {
  .modal-header {
    border-bottom: none;
    padding: 0;
    position: relative;
    height: 40px;

    .close-btn {
      position: absolute;
      top: 5px;
      right: 10px;
      background: transparent;
      border: none;
      font-size: 2.5rem;
      font-weight: 300;
      line-height: 1;
      color: #000;
      opacity: 0.5;
      cursor: pointer;
      padding: 5px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }

      &:focus {
        outline: none;
      }

      span {
        font-size: 2.5rem;
        display: block;
        line-height: 1;
      }
    }
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-content-wrapper {
    p {
      margin-bottom: 0;
      line-height: 1.6;

      strong {
        font-weight: bold;
      }
    }
  }

  .modal-content {
    border-radius: 8px;
    border: 2px solid #cfcfcf;
  }
}
</style>
